import React, { useState, useEffect, ReactElement, useRef } from "react";
import clsx from "clsx";
import { createPortal } from "react-dom";

import { ReactComponent as IconArrow } from "../../assets/images/AnswerList/icon_dropdown_arrow.svg";
import { ReactComponent as IconCheckOn } from "../../assets/images/AnswerList/icon_check_on.svg";
import useOnClickOutside from "../../hooks/useOnClickOutside";

import styles from "./DropdownMenu.module.scss";

export type Option<V extends string | number = number> = {
  value: V;
  label: string | ReactElement;
};

const BackDrop = () => {
  return createPortal(<div className={styles.backdrop} />, document.body);
};

export type Props<V extends string | number = number> = {
  options: Option<V>[];
  placeholder?: string;
  defaultValue?: Option<V>;
  className?: string;
  classNameOptions?: string;
  size?: "base" | "sm";
  backdrop?: boolean;
  openUpwards?: boolean;
  onChange: (value: V) => void;
};

const TEXT_NO_DATA = "データがありません";

export const DropdownMenu = <V extends string | number = number>({
  options,
  placeholder,
  defaultValue,
  className,
  classNameOptions,
  size = "base",
  backdrop,
  openUpwards,
  onChange,
}: Props<V>) => {
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [optionSelected, setOptionSelected] = useState<Option<V>>();

  useEffect(() => {
    if (
      defaultValue === undefined &&
      optionSelected === undefined &&
      options.length > 0 &&
      !placeholder
    ) {
      setOptionSelected(options[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, defaultValue]);

  useEffect(() => {
    if (optionSelected !== undefined) {
      onChange(optionSelected.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionSelected?.value]);

  useEffect(() => {
    if (defaultValue) {
      setOptionSelected({
        value: defaultValue.value,
        label: defaultValue.label,
      });
    }
  }, [defaultValue]);

  const labelChange = (option?: Option<V>) => {
    if (options.length === 0) return TEXT_NO_DATA;
    if (option === undefined) return placeholder || TEXT_NO_DATA;
    return options.some(o => o.value === option.value)
      ? option.label
      : placeholder || TEXT_NO_DATA;
  };

  const handleClickOutside = () => setOpen(false);

  useOnClickOutside(ref, handleClickOutside);

  return (
    <>
      <div
        ref={ref}
        className={clsx(
          styles.wrapSelect,
          styles[size],
          backdrop && open && styles.aboveBackdrop,
          className,
        )}
        onClick={() => setOpen(!open)}
      >
        <div className={styles.option}>
          <div className={clsx(styles.label, styles.ellipsis, styles[size])}>
            {labelChange(optionSelected)}
          </div>
          <IconArrow className={clsx(openUpwards && styles.upwardsArrowIcon)} />
        </div>
        {open && options.length !== 0 && (
          <div
            className={clsx(
              styles.options,
              openUpwards && styles.openUpwards,
              classNameOptions,
            )}
          >
            {options.map(item => (
              <div
                className={clsx(
                  styles.option,
                  styles[size],
                  item.value === optionSelected?.value && styles.isActive,
                )}
                onClick={() => {
                  setOptionSelected(item);
                }}
                key={item.value}
              >
                <div className={clsx(styles.label, styles[size])}>
                  {item.label}
                </div>
                <IconCheckOn className={styles.iconCheck} />
              </div>
            ))}
          </div>
        )}
      </div>
      {backdrop && open && <BackDrop />}
    </>
  );
};
export default DropdownMenu;
